import React, { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  InputAdornment,
  Button,
  Paper,
  Grid,
  Divider,
  Badge,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Search,
  DirectionsCarFilledOutlined,
  Delete,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { deleteVehicle, searchVehicles } from "../../api/vehicleApi";
import { VehicleData } from "../../types/vehicleData";
import { formatDate, formatDateTime } from "../../services/formatDate";
import Skeleton from "@mui/material/Skeleton";

interface Acao {
  id: number;
  atrasado: boolean;
  data_criacao?: string;
  checklist_respondido?: boolean;
  checklist?: string;
}

const ExcluirVeiculo: React.FC = () => {
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchResults, setSearchResults] = useState<VehicleData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [expandedPaperId, setExpandedPaperId] = useState<number | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [deletingVehicleId, setDeletingVehicleId] = useState<number | null>(
    null
  );

  const handlePaperClick = (id: number) => {
    setExpandedPaperId((prevId) => (prevId === id ? null : id));
  };

  const handleSearchChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setSearchValue(value);

    if (value.trim().length >= 3) {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const results = await searchVehicles(token, value);
          setSearchResults(results.data);
        } else {
          toast.error("Token não encontrado.");
        }
      } catch (error) {
        toast.error("Erro ao buscar veículos.");
      } finally {
        setLoading(false);
      }
    } else {
      setSearchResults([]);
    }
  };

  const handleDeleteVehicle = async (vehicleId: number) => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        await deleteVehicle(token, vehicleId);
        toast.success("Veículo excluído com sucesso.");
        setSearchResults([]); 
        setSearchValue(""); 
      } else {
        toast.error("Token não encontrado.");
      }
    } catch (error) {
      toast.error("Erro ao excluir veículo.");
    } finally {
      setConfirmDialogOpen(false);
    }
  };

  const handleOpenConfirmDialog = (vehicleId: number) => {
    setDeletingVehicleId(vehicleId);
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setDeletingVehicleId(null);
  };

  return (
    <Box p={3}>
      <Typography variant="h5" mb={3} fontWeight={400}>
        Excluir Veículo
      </Typography>
      <TextField
        placeholder="Buscar por Chassi/Modelo/Cliente"
        fullWidth
        sx={{
          "& .MuiInputLabel-root.Mui-focused": {
            color: "#303030",
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: 3,
            backgroundColor: "white",
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
            {
              borderColor: "#303030",
            },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        value={searchValue}
        onChange={handleSearchChange}
        disabled={loading}
      />
      <Typography variant="body2" color="textSecondary" mt={1} ml={1}>
        Informe 3 dígitos para pesquisa...
      </Typography>

      {loading ? (
        <Box mt={3}>
          {[...Array(2)].map((_, index) => (
            <Grid item xs={12} key={index} mt={2}>
              <Skeleton
                variant="rectangular"
                height={120}
                sx={{ borderRadius: 5, bgcolor: "grey.400" }}
              />
            </Grid>
          ))}
        </Box>
      ) : searchResults.length === 0 ? (
        <Typography variant="h6" mt={3} textAlign="center">
          {searchValue.length >= 3
            ? "Nenhum veículo encontrado."
            : "Nenhum veículo informado."}
        </Typography>
      ) : (
        searchResults.map((vehicle) => (
          <Grid item xs={12} key={vehicle.id} mb={1} mt={3}>
            <Paper
              elevation={4}
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                height: 80,
                borderRadius: 3,
                cursor: "pointer",
                backgroundColor: "#ffffff",
                position: "relative",
                zIndex: 2,
              }}
              onClick={() => handlePaperClick(vehicle.id)}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Badge
                  badgeContent={vehicle.acoes.length}
                  overlap="circular"
                  sx={{
                    "& .MuiBadge-badge": {
                      color: "white",
                      backgroundColor: vehicle.acoes.some(
                        (acao: Acao) => acao.atrasado
                      )
                        ? "red"
                        : "#1976d2",
                      marginLeft: -0.5,
                    },
                  }}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                >
                  <DirectionsCarFilledOutlined
                    sx={{
                      fontSize: { xs: 70, sm: 60, md: 80 },
                      color: vehicle.acoes.some((acao: Acao) => acao.atrasado)
                        ? "red"
                        : "#1976d2",
                    }}
                  />
                </Badge>
                <Divider
                  orientation="vertical"
                  variant="middle"
                  flexItem
                  sx={{ border: 1, ml: 2 }}
                />
                <Grid container spacing={1} wrap="nowrap" ml={0.5}>
                  <Grid item xs={12} sm={12} md={12}>
                    <Typography
                      sx={{ typography: { sm: "h5", xs: "caption" } }}
                    >
                      <strong>V.I.N:</strong> {vehicle.chassi_veiculo}
                    </Typography>
                    <Typography
                      sx={{ typography: { sm: "h5", xs: "caption" } }}
                    >
                      <strong>Modelo:</strong> {vehicle.descricao_modelo}
                    </Typography>
                    <Typography
                      sx={{ typography: { sm: "body2", xs: "caption" } }}
                    >
                      <strong>Empresa:</strong> {vehicle.empresa_nome}
                    </Typography>
                  </Grid>
                </Grid>
                <Box sx={{ marginLeft: "auto" }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => handleOpenConfirmDialog(vehicle.id)}
                    sx={{
                      bgcolor: "#f44336",
                      ":hover": { bgcolor: "#d32f2f" },
                      height: "100%",
                      width: 50,
                      minWidth: 47,
                    }}
                  >
                    <Delete />
                  </Button>
                </Box>
              </Box>
            </Paper>
            <Collapse
              in={expandedPaperId === vehicle.id}
              sx={{ borderRadius: 3, marginTop: -2 }}
            >
              <Paper
                elevation={4}
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  borderBottomLeftRadius: 9,
                  borderBottomRightRadius: 9,
                  marginTop: -2,
                  zIndex: 1,
                  transition: "height 0.2s ease-in-out",
                  height: "auto",
                  overflow: "hidden",
                  backgroundColor: "#E7E7E7",
                }}
              >
                <Grid container spacing={2} mt={0.5}>
                  <Grid item xs={12} sm={6} md={6}>
                    <Typography sx={{ display: "inline", marginRight: 1 }}>
                      Cor:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ display: "inline", fontWeight: "bold" }}
                    >
                      {vehicle.descricao_cor}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Typography sx={{ display: "inline", marginRight: 1 }}>
                      KM Inicial:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ display: "inline", fontWeight: "bold" }}
                    >
                      {vehicle.km_veiculo}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Typography sx={{ display: "inline", marginRight: 1 }}>
                      Código Rádio:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ display: "inline", fontWeight: "bold" }}
                    >
                      {vehicle.codigo_radio_veiculo}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Typography sx={{ display: "inline", marginRight: 1 }}>
                      Ano Veículo:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ display: "inline", fontWeight: "bold" }}
                    >
                      {vehicle.descricao_ano_modelo}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Typography sx={{ display: "inline", marginRight: 1 }}>
                      Data de Armazenagem:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ display: "inline", fontWeight: "bold" }}
                    >
                      {formatDate(vehicle.data_armazenagem_veiculo)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Typography sx={{ display: "inline", marginRight: 1 }}>
                      Local Armazenagem:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ display: "inline", fontWeight: "bold" }}
                    >
                      {vehicle.descricao_local}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container mt={3} spacing={2} justifyContent="center">
                  {vehicle.acoes.map((acao) => (
                    <Grid item xs={6} sm={6} md={6} key={acao.id}>
                      {acao.data_criacao ? (
                        <Typography
                          fontWeight="bold"
                          textAlign={"center"}
                          sx={{ fontSize: { xs: 12, sm: 12, md: 17, lg: 18 } }}
                        >
                          {formatDateTime(acao.data_criacao)}
                        </Typography>
                      ) : (
                        <Typography
                          fontWeight="bold"
                          textAlign={"center"}
                          sx={{ fontSize: { xs: 12, sm: 12, md: 17, lg: 18 } }}
                        >
                          Não Preenchido
                        </Typography>
                      )}
                      <Button
                        variant="contained"
                        fullWidth
                        sx={{
                          height: { xs: 60, sm: 50, md: 50 },
                          borderRadius: 2,
                          display: "flex",
                          ml: -0.5,
                          alignItems: "center",
                          justifyContent: "center",
                          ":hover": {
                            ...(acao.checklist_respondido
                              ? { bgcolor: "#4CAF50" }
                              : { bgcolor: "#F44336" }),
                          },
                          ...(acao.checklist_respondido
                            ? { bgcolor: "#4CAF50" }
                            : { bgcolor: "#F44336" }),
                        }}
                      >
                        <Typography variant="caption">
                          {acao.checklist}
                        </Typography>
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Collapse>
          </Grid>
        ))
      )}

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Tem certeza que deseja excluir?"}
        </DialogTitle>
        <DialogContent>
          {deletingVehicleId !== null && (
            <>
              <Typography variant="body1">
                <strong> {"Chassi do Veículo: "}</strong>
                {
                  searchResults.find(
                    (vehicle) => vehicle.id === deletingVehicleId
                  )?.chassi_veiculo
                }
              </Typography>
              <Typography variant="body1">
                <strong> {"Modelo: "}</strong>
                {
                  searchResults.find(
                    (vehicle) => vehicle.id === deletingVehicleId
                  )?.descricao_modelo
                }
              </Typography>
              <Typography variant="body1">
                <strong>{"Empresa: "}</strong>
                {
                  searchResults.find(
                    (vehicle) => vehicle.id === deletingVehicleId
                  )?.empresa_nome
                }
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary" variant="contained">
            Cancelar
          </Button>
          <Button
            onClick={() => handleDeleteVehicle(deletingVehicleId as number)}
            color="error"
            autoFocus
            variant="contained"
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExcluirVeiculo;
